import { useMemo } from 'react'

import { useGetVersionQuery } from '@sketch/gql-types'

import { useVersioning } from '../ShareVersionContext'
import { useComparedShortId } from './useCompareToggle'

/**
 * The two versions being compared, ordered rather than as given.
 *
 * Which of the pair the URL names is not which is older, so both the tinting
 * and the before/after labelling read from here instead of from the parameter.
 */
export interface ComparedVersions {
  /** Drawn in the "before" treatment. */
  older: { shortId: string; createdAt: string }
  /** The more recent of the two, whichever one is on screen. */
  newer: { shortId: string; createdAt: string }
  /**
   * Whether the version on screen is the newer of the two. False when the
   * reader is looking at an older version and comparing it forwards, which
   * leaves the canvas drawing the "before" side.
   */
  isViewingNewer: boolean
}

/**
 * The pair of versions being compared, or null when the reader is not
 * comparing.
 *
 * The URL names the other version, not the older one - which of the two is
 * older is decided here, so that a link someone pasted the other way round
 * still labels and tints the two sides correctly.
 *
 * `createdAt` decides it rather than `Version.order`, which is only populated
 * when a version arrives as part of the version history list; fetched on its
 * own, as the compared version is, it comes back null.
 */
export function useCompareVersions(): {
  versions: ComparedVersions | null
  loading: boolean
} {
  const { comparedShortId: compareShortId } = useComparedShortId()
  const versioning = useVersioning()

  const current = versioning.loading ? null : versioning.currentVersion

  // Comparing a version with itself is not a comparison. It happens on its own
  // when the reader opens the version they were already on.
  const isComparing = !!compareShortId && compareShortId !== current?.shortId

  // The full Version fragment rather than a leaner query of our own: it is what
  // every other version consumer asks for, so this is usually a cache hit, and
  // querying `presentationStatus` is what asks the render farm for the older
  // version's presentation file - which the before/after treatments will need
  // and which can take a while to arrive.
  const { data, loading } = useGetVersionQuery({
    variables: {
      shareIdentifier: versioning.share.identifier,
      versionShortId: compareShortId,
    },
    skip: !isComparing,
  })

  const other = data?.share?.version

  return useMemo(() => {
    if (!isComparing || !current) return { versions: null, loading: false }
    if (!other?.shortId || !other.createdAt) return { versions: null, loading }

    const viewing = { shortId: current.shortId!, createdAt: current.createdAt }
    const compared = { shortId: other.shortId, createdAt: other.createdAt }

    const isViewingNewer =
      new Date(viewing.createdAt).getTime() >=
      new Date(compared.createdAt).getTime()

    return {
      loading: false,
      versions: {
        older: isViewingNewer ? compared : viewing,
        newer: isViewingNewer ? viewing : compared,
        isViewingNewer,
      },
    }
  }, [isComparing, current, other, loading])
}
